import { api, buildStorefrontApiPath } from "@/lib/api";
import type { NotificationDto, NotificationListResponse } from "./types";

type Envelope<T> = {
  data?: T;
  meta?: Record<string, unknown>;
};

type RawNotificationsPayload =
  | NotificationListResponse
  | {
      items?: NotificationDto[];
      unreadCount?: number;
      page?: number;
      pageSize?: number;
      total?: number;
      totalPages?: number;
    };

function unwrap<T>(input: Envelope<T> | T): T {
  return ((input as Envelope<T>)?.data ?? input) as T;
}

function normalizeNotificationList(
  input: RawNotificationsPayload,
): NotificationListResponse {
  const payload = input ?? {};

  const items = Array.isArray((payload as any).items)
    ? (payload as any).items
    : [];
  const unreadCount = Number((payload as any).unreadCount ?? 0);
  const page = Number((payload as any).page ?? 1);
  const pageSize = Number((payload as any).pageSize ?? 20);
  const total = Number((payload as any).total ?? items.length);
  const totalPages = Number((payload as any).totalPages ?? 1);

  return {
    items,
    unreadCount,
    page,
    pageSize,
    total,
    totalPages,
  };
}

export async function fetchNotifications(
  params: {
    page?: number;
    pageSize?: number;
    onlyUnread?: boolean;
    signal?: AbortSignal;
  } = {},
  accessToken?: string | null,
): Promise<NotificationListResponse> {
  const { page = 1, pageSize = 20, onlyUnread, signal } = params;

  const searchParams = new URLSearchParams();
  searchParams.set("page", String(page));
  searchParams.set("pageSize", String(pageSize));
  if (onlyUnread) searchParams.set("onlyUnread", "true");

  const res = await api.get(
    `${buildStorefrontApiPath("/me/notifications")}?${searchParams.toString()}`,
    {
      signal,
      headers: accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : undefined,
    },
  );

  return normalizeNotificationList(unwrap<RawNotificationsPayload>(res.data));
}

export async function markNotificationRead(
  id: string,
  accessToken?: string | null,
): Promise<{ ok: true }> {
  const res = await api.post(
    buildStorefrontApiPath(`/me/notifications/${id}/read`),
    {},
    {
      headers: accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : undefined,
    },
  );

  return unwrap<{ ok: true }>(res.data);
}

export async function markAllNotificationsRead(
  accessToken?: string | null,
): Promise<{ ok: true }> {
  const res = await api.post(
    buildStorefrontApiPath("/me/notifications/mark-all-read"),
    {},
    {
      headers: accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : undefined,
    },
  );

  return unwrap<{ ok: true }>(res.data);
}

export async function fetchNotificationById(
  id: string,
  accessToken?: string | null,
): Promise<NotificationDto> {
  const res = await api.get(buildStorefrontApiPath(`/me/notifications/${id}`), {
    headers: accessToken
      ? { Authorization: `Bearer ${accessToken}` }
      : undefined,
  });

  return unwrap<NotificationDto>(res.data);
}